/**
 * Theme Classifier - Hybrid Edition
 * 
 * Uses semantic similarity when embeddings are available,
 * falls back to keyword matching otherwise.
 * 
 * TECHNOLOGY RULES:
 * - semanticSim >= 0.72 → ACCEPT
 * - strongTechHits >= 1 AND semanticSim >= 0.60 → ACCEPT  
 * - keywordScore >= 2 → ACCEPT
 * 
 * FASHION OVERRIDE (relaxed):
 * - Only if clothingHits >= 2 OR more lifestyle than tech keywords
 * - NOT applied if item mentions tablet/phone/laptop/screen/interface
 */

import { THEME_KEYWORDS } from "./themeKeywords";
import { cosineSimilarity } from "./embeddingUtils";

// Theme embeddings cache (pre-computed)
const THEME_EMBEDDINGS_CACHE = {};

// STRONG TECH KEYWORDS
const STRONG_TECH_KEYWORDS = new Set([
  "ai", "artificial intelligence", "software", "coding", "developer", 
  "programming", "api", "apis", "automation", "robotics", 
  "machine learning", "neural", "cyber", "cloud", "infrastructure", 
  "data science", "engineering", "algorithm", "algorithms", "backend",
  "frontend", "devops", "saas", "blockchain", "deep learning",
  "nlp", "computer vision", "startup tool", "tech startup"
]);

// NOISE KEYWORDS - ignored
const NOISE_KEYWORDS = new Set([
  "modern", "digital", "online", "photo", "photos", "image", "images",
  "picture", "pictures", "portrait", "selfie", "photography",
  "man", "woman", "male", "female", "person", "people", "human",
  "stairs", "balcony", "outdoors", "outside", "indoor", "indoors",
  "room", "living room", "sofa", "chair", "building", "street",
  "wall", "floor", "window", "door", "background",
  "pose", "posed", "posing", "standing", "sitting", "walking", "looking",
  "casual", "relaxed", "nice", "good", "great", "beautiful", "amazing"
]);

// FASHION/CLOTHING KEYWORDS
const FASHION_KEYWORDS = new Set([
  "fashion", "clothing", "outfit", "outfits", "wardrobe", "streetwear", 
  "menswear", "womenswear", "dress", "dresses", "jacket", "coat", 
  "blazer", "suit", "suits", "shirt", "hoodie", "sweater", "jeans", 
  "pants", "skirt", "heels", "boots", "sneakers", "shoes", "watch", 
  "bag", "handbag", "sunglasses", "hat", "model", "models", "editorial",
  "lookbook", "fit", "chic", "couture", "trend", "designer", "collection"
]);

// LIFESTYLE KEYWORDS
const LIFESTYLE_KEYWORDS = new Set([
  "lifestyle", "travel", "vacation", "coffee", "cafe", "restaurant",
  "gym", "wellness", "routine", "morning", "evening", "weekend",
  "nature", "beach", "mountain", "park", "garden", "home", "interior",
  "decor", "aesthetic", "composition", "style"
]);

// DEVICE KEYWORDS - prevent fashion override if present
const DEVICE_KEYWORDS = new Set([
  "tablet", "phone", "laptop", "screen", "interface", "device",
  "computer", "monitor", "display", "app", "software", "website"
]);

// Debug counter
let debugCount = 0;

/**
 * Count keywords from a set in text
 */
function countKeywords(text, keywordSet) {
  if (!text) return 0;
  const textLower = text.toLowerCase();
  let count = 0;
  for (const keyword of keywordSet) {
    if (textLower.includes(keyword)) count++;
  }
  return count;
}

/**
 * Extract all text content from an item
 */
function extractItemText(item) {
  const parts = [];
  if (item.title) parts.push(item.title);
  if (item.description) parts.push(item.description);
  
  const tagSources = [
    item.auto_tags?.topics,
    item.auto_tags?.themes,
    item.auto_tags?.categories,
    item.meta?.ai_analysis?.topics,
    item.meta?.ai_analysis?.themes,
    item.primary_tags,
    item.tags,
  ];
  
  for (const source of tagSources) {
    if (Array.isArray(source)) {
      for (const tag of source) {
        if (typeof tag === 'string') parts.push(tag);
      }
    }
  }
  
  return parts.join(' ').toLowerCase();
}

/**
 * Calculate semantic similarity if embeddings available
 * Returns 0 if embeddings not available
 */
function getSemanticSimilarity(item, theme) {
  // Check if item has stored embedding
  if (!item.embedding || !Array.isArray(item.embedding) || item.embedding.length === 0) {
    return 0;
  }
  
  // Check if theme embedding is cached
  const themeKey = theme.toLowerCase();
  const themeEmbedding = THEME_EMBEDDINGS_CACHE[themeKey];
  if (!themeEmbedding) {
    return 0;
  }
  
  // Calculate similarity
  return cosineSimilarity(item.embedding, themeEmbedding);
}

/**
 * Cache a theme embedding
 */
export function cacheThemeEmbedding(theme, embedding) {
  THEME_EMBEDDINGS_CACHE[theme.toLowerCase()] = embedding;
}

/**
 * Main function: Check if item matches a theme
 */
export function itemMatchesTheme(item, targetTheme, options = {}) {
  if (!item || !targetTheme) return false;
  
  const { debug = false } = options;
  const targetLower = targetTheme.toLowerCase();
  const text = extractItemText(item);
  const isImage = item.source_type === 'image';
  
  // Count various keyword types
  const strongTechHits = countKeywords(text, STRONG_TECH_KEYWORDS);
  const fashionHits = countKeywords(text, FASHION_KEYWORDS);
  const lifestyleHits = countKeywords(text, LIFESTYLE_KEYWORDS);
  const deviceHits = countKeywords(text, DEVICE_KEYWORDS);
  
  // Calculate keyword score for target theme
  const themeKeywords = THEME_KEYWORDS[targetTheme] || [];
  let keywordScore = 0;
  for (const keyword of themeKeywords) {
    const kw = keyword.toLowerCase();
    if (NOISE_KEYWORDS.has(kw)) continue;
    if (text.includes(kw)) keywordScore++;
  }
  
  // Get semantic similarity (0 if no embeddings)
  const semanticSim = getSemanticSimilarity(item, targetTheme);
  
  // Determine threshold based on item type
  const simThreshold = isImage ? 0.75 : 0.70;
  const simThresholdWithKeywords = isImage ? 0.65 : 0.60;
  
  let isMatch = false;
  let reason = '';
  let finalTheme = null;
  
  // TECHNOLOGY RULES
  if (targetLower === 'technology') {
    // Check fashion override (relaxed)
    // Only apply if: fashionHits >= 2 OR (lifestyleHits > strongTechHits)
    // BUT NOT if device keywords present
    const shouldOverride = (fashionHits >= 2 || lifestyleHits > strongTechHits) && deviceHits === 0;
    
    if (shouldOverride) {
      isMatch = false;
      reason = `Fashion override: ${fashionHits} fashion, ${lifestyleHits} lifestyle > ${strongTechHits} tech`;
      finalTheme = fashionHits > lifestyleHits ? 'Creativity' : 'Lifestyle';
    }
    // Rule 1: High semantic similarity
    else if (semanticSim >= 0.72) {
      isMatch = true;
      reason = `Semantic similarity ${semanticSim.toFixed(2)} >= 0.72`;
      finalTheme = 'Technology';
    }
    // Rule 2: Some tech hits + moderate semantic
    else if (strongTechHits >= 1 && semanticSim >= simThresholdWithKeywords) {
      isMatch = true;
      reason = `${strongTechHits} tech hits + semantic ${semanticSim.toFixed(2)} >= ${simThresholdWithKeywords}`;
      finalTheme = 'Technology';
    }
    // Rule 3: Strong keyword score
    else if (keywordScore >= 2) {
      isMatch = true;
      reason = `Keyword score ${keywordScore} >= 2`;
      finalTheme = 'Technology';
    }
    // Rule 4: At least 1 strong tech hit (fallback)
    else if (strongTechHits >= 2) {
      isMatch = true;
      reason = `${strongTechHits} strong tech hits >= 2`;
      finalTheme = 'Technology';
    }
    else {
      isMatch = false;
      reason = `No match: techHits=${strongTechHits}, keywords=${keywordScore}, sim=${semanticSim.toFixed(2)}`;
      finalTheme = 'General';
    }
  }
  // CREATIVITY
  else if (targetLower === 'creativity') {
    if (fashionHits >= 1) {
      isMatch = true;
      reason = `Fashion content (${fashionHits} hits)`;
      finalTheme = 'Creativity';
    } else if (semanticSim >= simThreshold) {
      isMatch = true;
      reason = `Semantic ${semanticSim.toFixed(2)} >= ${simThreshold}`;
      finalTheme = 'Creativity';
    } else if (keywordScore >= 1) {
      isMatch = true;
      reason = `Keyword score ${keywordScore}`;
      finalTheme = 'Creativity';
    } else {
      isMatch = false;
      reason = `No match`;
      finalTheme = 'General';
    }
  }
  // LIFESTYLE
  else if (targetLower === 'lifestyle') {
    if (lifestyleHits >= 1) {
      isMatch = true;
      reason = `Lifestyle content (${lifestyleHits} hits)`;
      finalTheme = 'Lifestyle';
    } else if (semanticSim >= simThreshold) {
      isMatch = true;
      reason = `Semantic ${semanticSim.toFixed(2)} >= ${simThreshold}`;
      finalTheme = 'Lifestyle';
    } else if (keywordScore >= 1) {
      isMatch = true;
      reason = `Keyword score ${keywordScore}`;
      finalTheme = 'Lifestyle';
    } else {
      isMatch = false;
      reason = `No match`;
      finalTheme = 'General';
    }
  }
  // OTHER THEMES
  else {
    if (semanticSim >= simThreshold) {
      isMatch = true;
      reason = `Semantic ${semanticSim.toFixed(2)} >= ${simThreshold}`;
      finalTheme = targetTheme;
    } else if (keywordScore >= 2) {
      isMatch = true;
      reason = `Keyword score ${keywordScore} >= 2`;
      finalTheme = targetTheme;
    } else if (keywordScore === 1 && (item.title || '').toLowerCase().includes(targetLower)) {
      isMatch = true;
      reason = `Score 1 + title match`;
      finalTheme = targetTheme;
    } else {
      isMatch = false;
      reason = `No match`;
      finalTheme = 'General';
    }
  }
  
  // Debug logging
  if (debug || (debugCount < 5 && import.meta.env.DEV)) {
    debugCount++;
    console.log(`\n🎯 Testing: "${item.title?.slice(0, 50)}"`);
    console.log(`→ FINAL THEME: ${finalTheme}`, {
      techSim: semanticSim.toFixed(3),
      techHits: strongTechHits,
      fashionHits,
      lifestyleHits,
      deviceHits,
      keywordScore,
      isImage,
      isMatch,
      reason
    });
  }
  
  return isMatch;
}

/**
 * Reset debug counter
 */
export function resetDebugCount() {
  debugCount = 0;
}

/**
 * Classify topics into a theme (legacy)
 */
export function classifyTheme(topics = []) {
  const fakeItem = { auto_tags: { topics }, title: '', description: '' };
  
  // Try each theme
  for (const theme of Object.keys(THEME_KEYWORDS)) {
    if (itemMatchesTheme(fakeItem, theme, { debug: false })) {
      return theme;
    }
  }
  return null;
}

/**
 * Get dominant theme (legacy)
 */
export function getDominantTheme(topics = []) {
  return classifyTheme(topics) || "General";
}

// Browser debugging
if (typeof window !== 'undefined') {
  window.debugThemeClassifier = {
    STRONG_TECH_KEYWORDS: Array.from(STRONG_TECH_KEYWORDS),
    FASHION_KEYWORDS: Array.from(FASHION_KEYWORDS),
    LIFESTYLE_KEYWORDS: Array.from(LIFESTYLE_KEYWORDS),
    DEVICE_KEYWORDS: Array.from(DEVICE_KEYWORDS),
    NOISE_KEYWORDS: Array.from(NOISE_KEYWORDS),
    THEME_EMBEDDINGS_CACHE,
    itemMatchesTheme,
    classifyTheme,
    getDominantTheme,
    cacheThemeEmbedding,
    resetDebugCount,
    getSemanticSimilarity,
    testItem: (item, theme) => {
      console.log('\n' + '='.repeat(60));
      resetDebugCount();
      const result = itemMatchesTheme(item, theme, { debug: true });
      console.log(`\n📋 RESULT: ${result ? '✅ MATCH' : '❌ REJECT'}`);
      return result;
    },
    testBatch: (items, theme) => {
      console.log(`\n🧪 Testing ${items.length} items against "${theme}":`);
      resetDebugCount();
      let matched = 0, rejected = 0;
      for (const item of items.slice(0, 10)) {
        if (itemMatchesTheme(item, theme, { debug: true })) matched++;
        else rejected++;
      }
      console.log(`\n📊 Summary: ${matched} matched, ${rejected} rejected`);
    }
  };
}
